import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook che persiste i filtri trasversali nell'URL come query params.
 *
 * Permette di salvare/condividere viste filtrate (es. /periodo/mese/2026-06?area=business&stato=raggiunto).
 *
 * Schema query string:
 *   area=business,lavoro      → array di stringhe
 *   stato=on_track,a_rischio  → idem
 *   confidence=alta,bassa     → idem
 *
 * Returns: [filters, setFilters]
 *  - filters: { area: [], stato: [], confidence: [] }
 *  - setFilters: (newFilters) => void
 */
const KEYS = ['area', 'stato', 'confidence'];

function parseList(s) {
  if (!s) return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

export function useUrlFilters() {
  const [search, setSearch] = useSearchParams();

  const filters = useMemo(() => ({
    area: parseList(search.get('area')),
    stato: parseList(search.get('stato')),
    confidence: parseList(search.get('confidence')),
  }), [search]);

  const setFilters = useCallback((next) => {
    // Manteniamo eventuali altri parametri (es. ?kr=ID) intatti.
    setSearch((prev) => {
      const sp = new URLSearchParams(prev);
      for (const k of KEYS) {
        const list = (next && next[k]) || [];
        if (list.length === 0) sp.delete(k);
        else sp.set(k, list.join(','));
      }
      return sp;
    }, { replace: true });
  }, [setSearch]);

  return [filters, setFilters];
}
